export type LabOrderStatus =
  | "requested"
  | "pending_insurer"
  | "in_progress"
  | "completed"
  | "cancelled";

export type InsurerApprovalStatus =
  | "none"
  | "pending"
  | "approved"
  | "rejected"
  | "self_pay";

/** Interprétation du résultat par rapport à la norme. */
export type LabResultFlag = "" | "normal" | "high" | "low" | "critical";

export interface LabResultLine {
  id: string;
  parameter: string;
  value: string;
  unit: string;
  referenceRange: string;
  flag: LabResultFlag;
}

export type LabResultLineInput = Omit<LabResultLine, "id">;

export interface LabOrder {
  id: string;
  orderNumber: string;
  patientId: string;
  consultationId: string | null;
  prescriberId: string;
  prescriber: string;
  testName: string;
  clinicalNotes: string;
  status: LabOrderStatus;
  insurerApproval: InsurerApprovalStatus;
  coverageCaseId: string | null;
  /** Lignes structurées (prioritaire à l'affichage). */
  resultLines: LabResultLine[];
  /** Texte libre — legacy ou commentaire global. */
  resultText: string;
  resultAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LabOrderInput {
  patientId: string;
  prescriberId: string;
  prescriber?: string;
  consultationId?: string | null;
  testName: string;
  clinicalNotes?: string;
}

export const LAB_STATUS_LABELS: Record<LabOrderStatus, string> = {
  requested: "Demandée",
  pending_insurer: "En attente assureur",
  in_progress: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
};

export const COMMON_LAB_TESTS = [
  "NFS (Numération formule sanguine)",
  "Glycémie à jeun",
  "Créatininémie",
  "Bilan hépatique",
  "Test paludisme (TDR)",
  "Groupe sanguin + Rhésus",
  "Urée / créatinine",
  "Ionogramme sanguin",
  "Bilan fertilité (FSH / LH)",
  "AMH (réserve ovarienne)",
  "Spermogramme",
] as const;

export function labOrderConsultationLabel(consultationId: string | null): string | null {
  if (!consultationId) return null;
  return `Consultation ${consultationId.replace(/^con-/, "").slice(0, 8)}…`;
}

export const LAB_RESULT_FLAG_LABELS: Record<LabResultFlag, string> = {
  "": "—",
  normal: "Normal",
  high: "Élevé",
  low: "Bas",
  critical: "Critique",
};

export interface LabResultLineTemplate {
  parameter: string;
  unit: string;
  referenceRange: string;
}

/** Modèles de lignes par type d'analyse courant. */
export const LAB_RESULT_LINE_PRESETS: Record<string, LabResultLineTemplate[]> = {
  nfs: [
    { parameter: "Hémoglobine", unit: "g/dL", referenceRange: "12 – 16" },
    { parameter: "Hématocrite", unit: "%", referenceRange: "36 – 48" },
    { parameter: "Leucocytes", unit: "/mm³", referenceRange: "4 000 – 10 000" },
    { parameter: "Plaquettes", unit: "/mm³", referenceRange: "150 000 – 400 000" },
    { parameter: "GR (globules rouges)", unit: "M/µL", referenceRange: "4.2 – 5.4" },
  ],
  glycemie: [
    { parameter: "Glycémie à jeun", unit: "g/L", referenceRange: "0.70 – 1.10" },
  ],
  creatinine: [
    { parameter: "Créatininémie", unit: "mg/L", referenceRange: "6 – 12" },
    { parameter: "DFG estimé", unit: "mL/min", referenceRange: "> 90" },
  ],
  paludisme: [
    { parameter: "TDR paludisme", unit: "", referenceRange: "Négatif" },
    { parameter: "Parasitémie", unit: "", referenceRange: "—" },
  ],
};

export function labResultPresetForTest(testName: string): LabResultLineTemplate[] {
  const lower = testName.toLowerCase();
  if (lower.includes("nfs") || lower.includes("numération")) return LAB_RESULT_LINE_PRESETS.nfs!;
  if (lower.includes("glycémie")) return LAB_RESULT_LINE_PRESETS.glycemie!;
  if (lower.includes("créatinine") || lower.includes("creatinine")) {
    return LAB_RESULT_LINE_PRESETS.creatinine!;
  }
  if (lower.includes("paludisme") || lower.includes("tdr")) {
    return LAB_RESULT_LINE_PRESETS.paludisme!;
  }
  return [{ parameter: "", unit: "", referenceRange: "" }];
}

const FLAG_VALUES = new Set<LabResultFlag>(["", "normal", "high", "low", "critical"]);

export function normalizeLabResultLine(raw: unknown, index: number): LabResultLine | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const parameter = String(r.parameter ?? "").trim();
  const value = String(r.value ?? "").trim();
  if (!parameter && !value) return null;
  const flag = String(r.flag ?? "");
  return {
    id: String(r.id ?? `lrl-${index}`),
    parameter,
    value,
    unit: String(r.unit ?? "").trim(),
    referenceRange: String(r.referenceRange ?? r.reference ?? "").trim(),
    flag: FLAG_VALUES.has(flag as LabResultFlag) ? (flag as LabResultFlag) : "",
  };
}

export function normalizeLabResultLines(raw: unknown): LabResultLine[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((line, i) => normalizeLabResultLine(line, i))
    .filter((line): line is LabResultLine => line != null);
}

export function createLabResultLineFromTemplate(
  template: LabResultLineTemplate,
  index: number,
): LabResultLine {
  return {
    id: `lrl-${Date.now()}-${index}`,
    parameter: template.parameter,
    value: "",
    unit: template.unit,
    referenceRange: template.referenceRange,
    flag: "",
  };
}

export function buildLabResultText(lines: LabResultLine[], comment = ""): string {
  const body = lines
    .filter((l) => l.parameter.trim() || l.value.trim())
    .map((line) => {
      const parts = [line.parameter, line.value, line.unit].filter(Boolean).join(" ");
      const norm = line.referenceRange ? ` (N: ${line.referenceRange})` : "";
      const flag =
        line.flag && line.flag !== "normal" ? ` [${LAB_RESULT_FLAG_LABELS[line.flag]}]` : "";
      return `${parts}${norm}${flag}`.trim();
    })
    .join("\n");
  const note = comment.trim();
  if (body && note) return `${body}\n\n${note}`;
  return body || note;
}

export function formatLabResultSummary(order: Pick<LabOrder, "resultLines" | "resultText" | "status">): string {
  if (order.resultLines.length > 0) {
    const preview = order.resultLines
      .filter((l) => l.value.trim())
      .slice(0, 3)
      .map((l) => `${l.parameter} ${l.value}${l.unit ? ` ${l.unit}` : ""}`)
      .join(" · ");
    const more =
      order.resultLines.filter((l) => l.value.trim()).length > 3
        ? ` (+${order.resultLines.filter((l) => l.value.trim()).length - 3})`
        : "";
    return preview ? `${preview}${more}` : "Résultats saisis";
  }
  if (order.resultText.trim()) return order.resultText.trim().slice(0, 120);
  if (order.status === "completed") return "Résultat validé";
  return "En attente";
}

export function getLabResultComment(order: Pick<LabOrder, "resultLines" | "resultText">): string {
  if (order.resultLines.length === 0) return "";
  const body = buildLabResultText(order.resultLines);
  const text = order.resultText.trim();
  if (!text || text === body) return "";
  if (text.startsWith(body)) return text.slice(body.length).trim();
  return "";
}

export function labResultAbnormalLines(lines: LabResultLine[]): LabResultLine[] {
  return lines.filter(
    (line) => line.flag === "high" || line.flag === "low" || line.flag === "critical",
  );
}

export function formatLabAbnormalSummary(lines: LabResultLine[]): string {
  const abnormal = labResultAbnormalLines(lines);
  if (abnormal.length === 0) return "";
  return abnormal
    .map((line) => {
      const flag = line.flag ? LAB_RESULT_FLAG_LABELS[line.flag] : "";
      return `${line.parameter} ${line.value}${line.unit ? ` ${line.unit}` : ""}${flag ? ` (${flag})` : ""}`;
    })
    .join(" · ");
}

export interface LabResultSaveInput {
  resultLines: LabResultLineInput[];
  resultText?: string;
}
